import React, { Component, Suspense } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import Helmet from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { withStyles } from '@material-ui/core/styles';
import { defaultCurrentDomainLogo } from 'containers/App/contexts/BrandingContext';

import DowntimeAlert from 'components/DowntimeAlert';
import ExpiringPasswordAlert from 'components/ExpiringPasswordAlert';
import InternetExplorerAlert from 'core/InternetExplorerAlert';
import { Snackbar, ErrorBoundaryApp } from 'core';
import { Embedor } from 'embeds/components';
import { initApp } from '@lib/actions';

import { selectAppReady, selectCurrentUser, selectMessage } from '@lib/selectors';

import Expiration from 'containers/Expiration';

import authResource from 'resources/authResource';
import storageService from '@lib/services/storageService';
import isOnEmbedPage from '@lib/utils/isOnEmbedPage';

import { getDowntimeMessage, getLanguages, hideExpiringPasswordAlert } from './actions';
import {
  makeSelectDowntimeMessage,
  makeSelectError,
  makeSelectLoading,
  makeSelectShowExpiringPasswordAlert,
} from './selectors';
import AppContent from './AppContent';
import BrandingProvider from './contexts/BrandingContext';
import { PageLoader, withAppWrapper, LoadingProgress, AppWebNotifications, AppNotifications } from './components';

const styles = (theme) => {
  const {
    custom: {
      palette: { colorSupportPowder, colorSupportSnow },
    },
    palette: {
      common: { white },
    },
    zIndex: { tooltip },
  } = theme;

  return {
    billingContent: {
      backgroundColor: colorSupportSnow,
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      overflowY: 'auto',
    },
    billingHeader: {
      background: white,
      boxShadow: '4px 4px 12px rgba(78, 97, 112, 0.15)',
      height: 72,
      padding: '20px 32px',
      position: 'absolute',
      top: 0,
      width: '100%',
    },
    content: {
      backgroundColor: colorSupportSnow,
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      overflowY: 'auto',
      overflowX: 'hidden',
      position: 'relative',
    },
    mobileBanner: {
      backgroundColor: colorSupportPowder,
      height: '100%',
      position: 'absolute',
      zIndex: tooltip,
    },
    root: {
      backgroundColor: colorSupportSnow,
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      height: '100%',
      width: '100%',
    },
    wrapper: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      maxHeight: '100%',
    },
    errorBoundary: {
      position: 'absolute',
    },
  };
};

class App extends Component {
  componentDidMount() {
    const { appReady, location, dispatchGetDowntimeMessage, dispatchInitApp } = this.props;
    if (!appReady) {
      dispatchInitApp({ location });
    }
    dispatchGetDowntimeMessage();
  }

  componentDidUpdate(prevProps) {
    const { currentUser, dispatchGetLanguages } = this.props;

    if (currentUser && !prevProps.currentUser) {
      dispatchGetLanguages();
    }
  }

  renderChildren() {
    const { children } = this.props;
    return <ErrorBoundaryApp>{React.Children.toArray(children)}</ErrorBoundaryApp>;
  }

  renderDowntimeAlert() {
    const { downtimeMessage, location } = this.props;

    if (!isEmpty(downtimeMessage) && location.pathname.indexOf('/login') >= 0) {
      return <DowntimeAlert message={downtimeMessage} />;
    }
  }

  renderLoading() {
    const { loading } = this.props;

    return <LoadingProgress loading={loading} />;
  }

  renderError() {
    const { message, error } = this.props;
    return (
      <>
        <Snackbar content={message} variant={message.variant} />
        {/* TODO: reuse message reducer to show errors */}
        <Snackbar content={error} variant="error" />
      </>
    );
  }

  renderExpiringPasswordAlert() {
    const { currentUser, dispatchHideExpiringPasswordAlert, showExpiringPasswordAlert } = this.props;

    if (!showExpiringPasswordAlert) return;

    return <ExpiringPasswordAlert currentUser={currentUser} onClose={dispatchHideExpiringPasswordAlert} />;
  }

  renderApp() {
    const { appReady, children, classes, currentUser, location, brandingContextValues } = this.props;
    const { isLoading, isCobrandingEnabled, cobrandingCustomStyles } = brandingContextValues;
    const isNotFound = get(children, 'type.name') === 'NotFound';
    const cobrandingUiSettings = cobrandingCustomStyles?.styles?.theme?.uiSettings ?? {};
    const hideZenDeskHelp = !cobrandingUiSettings.enableZenDeskChat && isCobrandingEnabled;
    // RENDER EMBEDDED
    if (isOnEmbedPage()) {
      if (/\/embed\//.test(window.location.href)) {
        return (
          <>
            <Expiration pathOnExpire={window.location.href} />
            {this.renderChildren()}
            {this.renderError()}
            <AppNotifications />
          </>
        );
      }
      return (
        <Embedor>
          <Expiration pathOnExpire={window.location.href} />
          <Suspense fallback={null}>{this.renderChildren()}</Suspense>
          {this.renderError()}
          <AppNotifications />
        </Embedor>
      );
    }

    if (/\/(formAnswers|lumabot-session)\/[\w]+\/print/.test(location.pathname)) {
      return <div className={classes.root}>{this.renderChildren()}</div>;
    }

    const shouldHideHeaderWhenHasSchedulerOnPathname =
      location.pathname.indexOf('/scheduler') === 0 && location.pathname !== '/scheduler-assist';

    if (
      location.pathname.indexOf('/login') >= 0 ||
      location.pathname.indexOf('/zendesk/login') >= 0 ||
      location.pathname.indexOf('/demo-setup') >= 0 ||
      location.pathname.indexOf('/forgot-password') >= 0 ||
      location.pathname.startsWith('/v2/formBuilder') ||
      location.pathname.indexOf('/reset-password') >= 0 ||
      location.pathname.indexOf('/signup') >= 0 ||
      /\/groups\/[\w]+\/join\//.test(location.pathname) ||
      /\/join\//.test(location.pathname) ||
      shouldHideHeaderWhenHasSchedulerOnPathname ||
      location.pathname.indexOf('/practice') === 0 ||
      location.pathname.indexOf('/unsubscribe') === 0 ||
      /\/formAnswers\/[\w]+\/print/.test(location.pathname) ||
      // Hide the header and the drawer for HubJr mobile friendly page
      // https://lumahealth.clickup.com/t/864dp0p84
      sessionStorage.getItem('mainScreenMobile') ||
      isNotFound
    ) {
      return (
        <div className={classes.root}>
          <InternetExplorerAlert />
          {this.renderDowntimeAlert()}
          <div className={classes.content}>{this.renderChildren()}</div>
          <AppNotifications />
          {this.renderError()}
        </div>
      );
    }

    if (location.pathname.indexOf('/billing/checkout') >= 0) {
      return (
        <div className={classes.root}>
          <div className={classes.billingContent}>
            <div className={classes.billingHeader}>
              <img src={defaultCurrentDomainLogo} alt="Luma Health" width="115" />
            </div>
            {this.renderChildren()}
          </div>
          {this.renderError()}
        </div>
      );
    }

    if (!appReady) {
      return <div />;
    }

    if (location.pathname.startsWith('/telehealth') || location.pathname.startsWith('/v2/telehealth')) {
      return (
        <div className={classes.root}>
          <InternetExplorerAlert />
          {this.renderDowntimeAlert()}
          <AppContent className={classes.content} pathname={location.pathname}>
            {this.renderChildren()}
          </AppContent>
          <AppNotifications />
          {this.renderError()}
        </div>
      );
    }

    if (isLoading && isCobrandingEnabled) {
      return <PageLoader />;
    }

    if (currentUser) {
      return (
        <div className={classes.root}>
          <Expiration pathOnExpire="/login" />
          <InternetExplorerAlert />
          {this.renderExpiringPasswordAlert()}
          <Helmet
            titleTemplate="%s - Luma Health"
            defaultTitle="Luma Health"
            meta={[{ name: 'description', content: 'A React.js application for Luma Health' }]}
          />
          <div className={classes.wrapper}>
            <AppContent
              className={classes.content}
              pathname={location.pathname}
              withZendesk={!hideZenDeskHelp}
              withHeader
            >
              {children}
            </AppContent>
          </div>
          <AppNotifications />
          {this.renderLoading()}
          {this.renderError()}
          <AppWebNotifications location={location} />
        </div>
      );
    }

    return false;
  }

  render() {
    const { classes } = this.props;
    return <ErrorBoundaryApp className={classes.errorBoundary}>{this.renderApp()}</ErrorBoundaryApp>;
  }
}

App.propTypes = {
  appReady: PropTypes.bool.isRequired,
  children: PropTypes.node,
  classes: PropTypes.object.isRequired,
  message: PropTypes.object.isRequired,
  currentUser: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  dispatchInitApp: PropTypes.func.isRequired,
  dispatchHideExpiringPasswordAlert: PropTypes.func.isRequired,
  dispatchGetDowntimeMessage: PropTypes.func.isRequired,
  downtimeMessage: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.bool, PropTypes.string]),
  location: PropTypes.object,
  loading: PropTypes.bool,
  showExpiringPasswordAlert: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  appReady: selectAppReady,
  currentUser: selectCurrentUser,
  downtimeMessage: makeSelectDowntimeMessage(),
  error: makeSelectError(),
  loading: makeSelectLoading(),
  message: selectMessage,
  showExpiringPasswordAlert: makeSelectShowExpiringPasswordAlert(),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchInitApp: (payload) => dispatch(initApp.request(payload)),
  dispatchGetDowntimeMessage: () => {
    dispatch(getDowntimeMessage());
  },
  dispatchGetLanguages: () => {
    dispatch(getLanguages());
  },
  dispatchHideExpiringPasswordAlert: () => {
    dispatch(hideExpiringPasswordAlert());
  },
});

const AppWrapped = withAppWrapper(App);

const AppWithBrandingProvider = (props) => (
  <BrandingProvider>
    <AppWrapped {...props} />
  </BrandingProvider>
);

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect, withStyles(styles))(AppWithBrandingProvider);
